import React, { useState, useEffect, useRef } from 'react';

const MATRIX_CHARS = '01アイウエオカキサシスセタチツテナニヌネ<>{}[]|\\/*+-=~';

const TAG_COLORS = {
  AUCTION: '#c9a227',
  MARKET:  '#185FA5',
  RECORD:  '#C0392B',
  TREND:   '#0F6E56',
  DATA:    '#3C3489',
  BRAND:   '#0a5a80',
  SUPPLY:  '#7a5c00',
  NEWS:    '#445544',
};

const FALLBACK = {
  headline: "Rolex GMT-Master II 'Pepsi' Demand Surges as Secondary Market Tightens",
  summary: "The Rolex GMT-Master II ref. 126710BLNR — known as the 'Pepsi' — has seen a notable uptick in secondary market demand over the past 30 days. Average sold prices have climbed to $24,800, up from $23,400 in the prior period, with waitlists extending beyond 18 months at authorized retailers. Analysts attribute the move to renewed collector interest in steel sports watches following a quieter correction period in 2024.",
  source: "Dial Market Intelligence",
  date: new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
  tag: "MARKET",
  url: null,
  source_type: 'fallback',
};

function useTypewriter(text, started, speed = 18) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const iRef = useRef(0);

  useEffect(() => {
    if (!started || !text) return;
    iRef.current = 0;
    setDisplayed('');
    setDone(false);
    const interval = setInterval(() => {
      iRef.current += 1;
      setDisplayed(text.slice(0, iRef.current));
      if (iRef.current >= text.length) { setDone(true); clearInterval(interval); }
    }, speed);
    return () => clearInterval(interval);
  }, [text, started, speed]);

  return { displayed, done };
}

function MatrixStrip() {
  const cols = 64;
  const [chars, setChars] = useState(() =>
    Array.from({ length: cols }, () => ({
      char: MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)],
      bright: Math.random() > 0.8,
    }))
  );
  useEffect(() => {
    const t = setInterval(() => {
      setChars(prev => prev.map(c => ({
        char: Math.random() > 0.65 ? MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)] : c.char,
        bright: Math.random() > 0.78,
      })));
    }, 100);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ padding: '3px 14px', background: '#020d02', borderLeft: '0.5px solid #0a3a0a', borderRight: '0.5px solid #0a3a0a', overflow: 'hidden', whiteSpace: 'nowrap' }}>
      {chars.map((c, i) => (
        <span key={i} style={{
          fontFamily: "'Space Mono', monospace",
          fontSize: 8, color: c.bright ? '#00ff41' : '#003b0f',
          letterSpacing: '0.05em', userSelect: 'none',
        }}>{c.char}</span>
      ))}
    </div>
  );
}

export default function WatchNewsFeed() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [time, setTime] = useState(new Date());

  const { displayed: summaryText, done: summaryDone } = useTypewriter(data?.summary || '', started, 16);
  const { displayed: headlineText } = useTypewriter(data?.headline || '', started, 28);

  useEffect(() => {
    fetch('/.netlify/functions/watch-news')
      .then(r => r.json())
      .then(d => { setData(d); })
      .catch(() => setData(FALLBACK))
      .finally(() => { setLoading(false); setTimeout(() => setStarted(true), 400); });
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const article = data || FALLBACK;
  const tagColor = TAG_COLORS[article.tag] || TAG_COLORS.NEWS;

  return (
    <div style={{ marginBottom: 32 }}>

      {/* Terminal header bar */}
      <div style={{
        background: '#050f05', border: '0.5px solid #0a3a0a',
        borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
        padding: '10px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57' }} />
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#ffbd2e' }} />
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#28ca41' }} />
          </div>
          <span style={{ fontSize: 10, color: '#00cc34', fontFamily: "'Space Mono', monospace", letterSpacing: '0.07em' }}>
            DIAL_MARKET_INTELLIGENCE
          </span>
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <span style={{ fontSize: 9, color: article.source_type === 'live' ? '#00cc34' : '#004d10', fontFamily: "'Space Mono', monospace" }}>
            {article.source_type === 'live' ? '● LIVE' : '● DAILY BRIEF'}
          </span>
          <span style={{ fontSize: 9, color: '#003a0c', fontFamily: "'Space Mono', monospace" }}>
            {time.toLocaleTimeString('en-US', { hour12: false })}
          </span>
        </div>
      </div>

      {/* Matrix strip */}
      <MatrixStrip />

      {/* Main article card */}
      <div style={{
        background: '#020b02', border: '0.5px solid #0a3a0a',
        borderTop: 'none', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
        padding: '20px 20px 16px',
      }}>
        {/* Boot line */}
        <div style={{ fontSize: 9, color: '#004d10', fontFamily: "'Space Mono', monospace", marginBottom: 16 }}>
          {'>'} {loading ? 'SCANNING MARKET FEEDS…  █' : 'ARTICLE ACQUIRED — GENERATING ANALYSIS…'}
        </div>

        {/* Tag + date */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 3,
            border: `0.5px solid ${tagColor}`, color: tagColor,
            fontFamily: "'Space Mono', monospace", letterSpacing: '0.07em',
            background: `${tagColor}18`,
          }}>{article.tag}</span>
          <span style={{ fontSize: 9, color: '#004d10', fontFamily: "'Space Mono', monospace" }}>
            {article.date}
          </span>
        </div>

        {/* Headline */}
        <div style={{
          fontSize: 14, fontWeight: 600, color: '#00e03a',
          fontFamily: "'Space Grotesk', sans-serif",
          lineHeight: 1.45, marginBottom: 14,
          minHeight: 42,
          cursor: article.url ? 'pointer' : 'default',
        }}
          onClick={() => article.url && window.open(article.url, '_blank')}
        >
          {started ? headlineText : article.headline}
          {started && headlineText.length < (article.headline || '').length && (
            <span style={{ color: '#00ff41', animation: 'blink 0.7s step-end infinite' }}>█</span>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: '0.5px', background: '#0a2a0a', marginBottom: 14 }} />

        {/* AI Summary */}
        <div style={{
          fontSize: 12, color: '#00993a', fontFamily: "'Space Mono', monospace",
          lineHeight: 1.75, marginBottom: 16, minHeight: 60,
        }}>
          {'> '}{started ? summaryText : (article.summary || '')}
          {started && !summaryDone && (
            <span style={{ color: '#00ff41' }}>█</span>
          )}
        </div>

        {/* Footer — source */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingTop: 10, borderTop: '0.5px solid #0a2a0a', flexWrap: 'wrap', gap: 6,
        }}>
          <span style={{ fontSize: 9, color: '#004d10', fontFamily: "'Space Mono', monospace" }}>
            {'>'} SOURCE: {(article.source || 'Dial Market Intelligence').toUpperCase()}
          </span>
          <span style={{ fontSize: 9, color: '#003a0c', fontFamily: "'Space Mono', monospace" }}>
            AI-SUMMARISED · dialmarket.network
          </span>
        </div>
      </div>

      <style>{`
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
      `}</style>
    </div>
  );
}
